// Client-side assessment state. The review runs entirely in the browser
// (anonymous) and is persisted to localStorage so a refresh doesn't lose
// progress. Identity is captured only at the end (the report gate).

import type { Answer, PillarId, ReviewContact, ReviewSummary, ScoreValue, Selection } from '@dwa/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ReportData {
  contact: ReviewContact;
  summary: ReviewSummary;
}

interface AssessmentState {
  /** Which WAR is being taken (slug, e.g. "databricks"); null before one is chosen. */
  focus: string | null;
  pillars: PillarId[];
  answers: Record<string, Answer>;
  report: ReportData | null;
  /** Switch WARs: sets focus and clears prior selection/answers when it changes. */
  setFocus: (focus: string) => void;
  setPillars: (pillars: PillarId[]) => void;
  setAnswer: (questionId: string, score: ScoreValue, notes: string) => void;
  setReport: (report: ReportData) => void;
  reset: () => void;
}

export const useAssessmentStore = create<AssessmentState>()(
  persist(
    (set) => ({
      focus: null,
      pillars: [],
      answers: {},
      report: null,
      setFocus: (focus) =>
        set((s) => (s.focus === focus ? {} : { focus, pillars: [], answers: {}, report: null })),
      setPillars: (pillars) => set({ pillars }),
      setAnswer: (questionId, score, notes) =>
        set((s) => ({ answers: { ...s.answers, [questionId]: { questionId, score, notes } } })),
      setReport: (report) => set({ report }),
      reset: () => set({ focus: null, pillars: [], answers: {}, report: null }),
    }),
    { name: 'dwa-assessment' },
  ),
);

export const selectionOf = (pillars: PillarId[]): Selection => ({ pillars });
export const answerList = (answers: Record<string, Answer>): Answer[] => Object.values(answers);
